/**
 * Ajustes → Correo saliente (SMTP del bufete). Solo ADMIN.
 * Se usa para enviar presupuestos (y futuros avisos) por email.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { AlertCircle, CheckCircle2, Loader2, Save, Send } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import type { FirmSmtpStatus } from '@toga/shared'

const inputCls =
  'w-full px-2.5 py-1.5 rounded-lg bg-slate-900/40 border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50'

export default function EmailSettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const [host, setHost] = useState('')
  const [port, setPort] = useState('465')
  const [secure, setSecure] = useState(true)
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [hasPassword, setHasPassword] = useState(false)
  const [fromEmail, setFromEmail] = useState('')
  const [fromName, setFromName] = useState('')
  const [testTo, setTestTo] = useState('')

  useEffect(() => {
    api
      .get<FirmSmtpStatus>('/firm/smtp')
      .then((s) => {
        setHost(s.smtpHost ?? '')
        setPort(String(s.smtpPort))
        setSecure(s.smtpSecure)
        setUser(s.smtpUser ?? '')
        setHasPassword(s.hasPassword)
        setFromEmail(s.smtpFromEmail ?? '')
        setFromName(s.smtpFromName ?? '')
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'))
      .finally(() => setLoading(false))
  }, [])

  const onSave = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setOkMsg(null)
    try {
      const s = await api.put<FirmSmtpStatus>('/firm/smtp', {
        smtpHost: host.trim() || null,
        smtpPort: Number(port) || 465,
        smtpSecure: secure,
        smtpUser: user.trim() || null,
        // undefined = conservar la contraseña actual.
        ...(password ? { smtpPassword: password } : {}),
        smtpFromEmail: fromEmail.trim() || null,
        smtpFromName: fromName.trim() || null,
      })
      setHasPassword(s.hasPassword)
      setPassword('')
      setOkMsg('Configuración guardada.')
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error al guardar'))
    } finally {
      setSaving(false)
    }
  }

  const onTest = async () => {
    setTesting(true)
    setError(null)
    setOkMsg(null)
    try {
      const r = await api.post<{ ok: boolean; to: string }>('/firm/smtp/test', {
        ...(testTo.trim() ? { to: testTo.trim() } : {}),
      })
      setOkMsg(`Mensaje de prueba enviado a ${r.to}. Revisa la bandeja de entrada.`)
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error en la prueba'))
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">Correo saliente</h2>
        <p className="text-sm t-muted mt-1">
          Servidor SMTP con el que Toga envía presupuestos y avisos por email en nombre del
          bufete. La contraseña se guarda cifrada.
        </p>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {okMsg && (
        <div className="surface p-3 text-sm rounded-lg border text-emerald-300 bg-emerald-500/10 border-emerald-500/30 flex items-center gap-2">
          <CheckCircle2 size={14} /> {okMsg}
        </div>
      )}

      <form onSubmit={onSave} className="surface p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs t-muted mb-1">Servidor SMTP</label>
          <input
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="smtp.miproveedor.com"
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1">Puerto</label>
            <input
              type="number"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              className={inputCls}
            />
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={secure}
              onChange={(e) => setSecure(e.target.checked)}
            />
            TLS/SSL
          </label>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Usuario</label>
          <input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="usuario@miproveedor.com"
            autoComplete="off"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">
            Contraseña {hasPassword && <span className="t-faint">(guardada — escribe para cambiarla)</span>}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={hasPassword ? '••••••••' : 'Contraseña SMTP'}
            autoComplete="new-password"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Email remitente</label>
          <input
            type="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder="despacho@mibufete.es"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Nombre remitente</label>
          <input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="Bufete García & Asociados"
            className={inputCls}
          />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar
          </button>
        </div>
      </form>

      <div className="surface p-4 space-y-3">
        <h3 className="t-primary font-semibold text-sm">Probar el envío</h3>
        <p className="text-xs t-muted">
          Comprueba conexión, autenticación y entrega enviando un mensaje de prueba. Si lo dejas
          vacío se envía al propio remitente.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="destinatario@ejemplo.com (opcional)"
            className={inputCls}
          />
          <button
            type="button"
            disabled={testing}
            onClick={() => void onTest()}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5 whitespace-nowrap"
          >
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Enviar prueba
          </button>
        </div>
      </div>
    </div>
  )
}
